import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
  pdsTv,
} from '@nestle-npus-nbm/pds-ui';
import { useLocation } from '@reach/router';
import * as React from 'react';
import { useActiveKey } from 'src/hooks/useActiveKey';

type Props = {
  accordionList: {
    question: String;
    answer: {
      processed: string;
      format: TextFormat;
    };
    component_id: string;
    id: string;
  }[];
} & React.ComponentPropsWithoutRef<typeof Accordion>;

export const FaqAccordion = ({
  onValueChange,
  type = 'single',
  accordionList,
  ...props
}: Props) => {
  const { activeKey, setActiveKey } = useActiveKey(accordionList);
  const location = useLocation();

  // Update URL hash to trigger scroll only when one accordion is open. This will prevent the
  // browser from scrolling to the incorrect position due to the closing animation of the accordions.
  React.useEffect(() => {
    if (location.hash) {
      const internalActiveKey = decodeURIComponent(location.hash).replace('#', '');
      const activeAccordionItem = accordionList.find(item => {
        return item.component_id === internalActiveKey;
      });

      if (!activeAccordionItem) return;

      const hash = activeAccordionItem.component_id;
      if (hash) {
        const trigger = document.querySelector<HTMLButtonElement>(
          `[data-accordion-trigger="${hash}"]`,
        );
        if (trigger) {
          trigger.focus();
        }
      }
    }
  }, [accordionList, location.hash]);

  // If all the accordion sets are draft, don't render.
  if (!accordionList || accordionList.length === 0) {
    return null;
  }

  const handleActiveKey = (key: string & string[]) => {
    setActiveKey(key);

    if (type === 'single') {
      const activeAccordionItem = accordionList.find(item => item.id === key);

      if (activeAccordionItem?.component_id) {
        setTimeout(() => {
          window.location.hash = `#${activeAccordionItem.component_id}`;

          const trigger = document.querySelector<HTMLButtonElement>(
            `[data-accordion-trigger="${activeAccordionItem.component_id}"]`,
          );
          if (trigger) {
            trigger.focus();
          }
        }, 280);
      }
    }

    if (onValueChange) {
      onValueChange(key);
    }
  };

  return (
    <Accordion
      type={type}
      collapsible
      {...props}
      value={activeKey}
      onValueChange={handleActiveKey}
    />
  );
};

type AccordionItemProps = {
  id: string;
  value: string;
  title: string;
  children?: React.ReactNode;
};

export const FaqAccordionItem = ({ id, value, title, children }: AccordionItemProps) => {
  return (
    <AccordionItem value={value} className="first:pds-border-t">
      <div id={id} className="[&_>_*]:pds-mb-0">
        <AccordionTrigger
          data-accordion-trigger={id}
          className="pds-gap-4 pds-px-4 pds-py-5 pds-text-title-md data-[state=open]:pds-bg-surface-layer"
        >
          {title}
        </AccordionTrigger>
      </div>

      <AccordionContent forceMount className="pds-px-4 pds-py-5">
        {children}
      </AccordionContent>
    </AccordionItem>
  );
};

export const PurinaAccordion = Accordion;
export const PurinaAccordionItem = AccordionItem;
export const PurinaAccordionHeader = AccordionHeader;

const accordionTriggerStyles = pdsTv({
  base: 'pds-px-4 pds-py-4.5 pds-text-title-md pds-font-extrabold data-[state=open]:pds-bg-surface-layer',
});
const accordionContentStyles = pdsTv({
  base: 'pds-relative pds-px-4 pds-py-4.5',
});

export const PurinaAccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionTrigger>,
  React.ComponentPropsWithoutRef<typeof AccordionTrigger>
>(({ className, children, ...props }, ref) => {
  return (
    <AccordionTrigger ref={ref} className={accordionTriggerStyles({ className })} {...props}>
      {children}
    </AccordionTrigger>
  );
});

export const PurinaAccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionContent>,
  React.ComponentPropsWithoutRef<typeof AccordionContent>
>(({ className, children, ...props }, ref) => {
  return (
    <AccordionContent ref={ref} className={accordionContentStyles({ className })} {...props}>
      {children}
    </AccordionContent>
  );
});
